import {useEffect} from 'react';

import {isNil} from 'ramda';

import type {FetchState} from '../store/createEagGridStore';
import type {ActionDefinitionObject} from '../types/ActionDefinition';
import type {GridDataChange} from '../types/GridDataChange';
import type {RowData} from '../types/RowData';
import {useLatestRef} from './useLatestRef';

type UseGridDataNotificationParams = {
  rows: RowData[];
  fetchState: FetchState;
  actionsDefinition: ActionDefinitionObject;
  onGridDataChange: ((data: GridDataChange) => void) | undefined;
};

/**
 * Fires the host's onGridDataChange with the currently loaded rows and their fetch
 * state whenever either changes (initial load, refetch start/settle, pagination,
 * filter change). Pairs with selectDatagridRows: the host analyzes the exported rows
 * (e.g. via getRowCellValue) and selects them back through the external channel.
 */
export const useGridDataNotification = (params: UseGridDataNotificationParams): void => {
  const onGridDataChangeRef = useLatestRef(params.onGridDataChange);

  // eslint-disable-next-line eslint-js/no-restricted-syntax -- intentional: reactive host notification; must fire on every rows/fetchState transition (load, refetch start/settle, pagination, filter change), not just user events — mirrors useGridSelection's onRowSelectionChange effect
  useEffect(
    function notifyGridDataChange() {
      if (isNil(onGridDataChangeRef.current)) {
        return;
      }
      onGridDataChangeRef.current({
        rows: params.rows,
        fetchState: params.fetchState,
        actionsDefinition: params.actionsDefinition,
      });
    },
    [params.rows, params.fetchState, params.actionsDefinition] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );
};
